"use client";

import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { cn } from "@/lib/utils";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as React from "react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "../Drawer/Drawer";

export type PopoverContentContextType = {
  isMobile: boolean;
  isResponsive: boolean;
};

const PopoverContext = React.createContext<PopoverContentContextType>({
  isMobile: false,
  isResponsive: false,
});

export type PopoverProps = PopoverPrimitive.PopoverProps & {
  isResponsive?: boolean;
};

const Popover: React.FC<PopoverProps> = ({
  isResponsive = false,
  ...props
}) => {
  const isMobile = useMediaQuery("(max-width: 576px)", {
    isActive: isResponsive,
  });
  const [open, setOpen] = React.useState(false);

  return (
    <PopoverContext.Provider value={{ isMobile, isResponsive }}>
      {isResponsive && isMobile ? (
        <Drawer open={open} onOpenChange={setOpen} {...props} />
      ) : (
        <PopoverPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
      )}
    </PopoverContext.Provider>
  );
};
Popover.displayName = "Popover";

const PopoverTrigger = (props: PopoverPrimitive.PopoverTriggerProps) => {
  const { isMobile, isResponsive } = React.useContext(PopoverContext);

  if (isResponsive && isMobile) {
    return <DrawerTrigger {...props} />;
  }

  return <PopoverPrimitive.Trigger {...props} />;
};
PopoverTrigger.displayName = "PopoverTrigger";

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, ...props }, ref) => {
  const { isMobile, isResponsive } = React.useContext(PopoverContext);

  if (isResponsive && isMobile) {
    return (
      <DrawerContent
        {...props}
        className={cn("!tw-w-screen !tw-max-w-[100vw]", className)}
      />
    );
  }

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "tw-outline-none tw-z-max tw-rounded-8 tw-shadow-xl data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[side=bottom]:tw-slide-in-from-top-2 data-[side=left]:tw-slide-in-from-right-2 data-[side=right]:tw-slide-in-from-left-2 data-[side=top]:tw-slide-in-from-bottom-2",
          className,
        )}
        {...props}
      ></PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = "PopoverContent";

const PopoverClose: React.ForwardRefExoticComponent<
  PopoverPrimitive.PopoverCloseProps & React.RefAttributes<HTMLButtonElement>
> = React.forwardRef(({ ...props }, ref) => {
  const { isMobile, isResponsive } = React.useContext(PopoverContext);

  if (isResponsive && isMobile) {
    return <DrawerClose {...props} />;
  }

  return (
    <PopoverPrimitive.PopoverClose ref={ref} {...props} aria-label="close" />
  );
});
PopoverClose.displayName = "PopoverClose";

const PopoverArrow: React.FC<PopoverPrimitive.PopoverArrowProps> = ({
  ...props
}) => {
  const { isMobile, isResponsive } = React.useContext(PopoverContext);

  if (isResponsive && isMobile) {
    return null;
  }

  return <PopoverPrimitive.Arrow {...props} />;
};
PopoverArrow.displayName = "PopoverArrow";

export { Popover, PopoverArrow, PopoverClose, PopoverContent, PopoverTrigger };
